import { useState, useEffect } from "react";
import { LogOut, LucideIcon, Sun, Moon, Plus, CreditCard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import logo from "@/assets/new-logo-2.png";
import { ViewType } from "@/stores/useConfigStore";
import UpgradeDialog from "@/components/UpgradeDialog";
import { useAccountActions } from "@/hooks/useAccountActions";

export type { ViewType };

type NavButtonProps = {
  icon: LucideIcon;
  label: string;
  isActive: boolean;
  onClick: () => void;
  id?: string;
};

const NavButton = ({ icon: Icon, label, isActive, onClick, id }: NavButtonProps) => (
  <Tooltip>
    <TooltipTrigger asChild>
      <Button
        id={id}
        variant="ghost"
        size="icon"
        onClick={onClick}
        className={`w-10 h-10 rounded-full ${
          isActive
            ? "bg-blue-600 text-white hover:bg-blue-600 hover:text-white"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        }`}
      >
        <Icon className="h-5 w-5" />
      </Button>
    </TooltipTrigger>
    <TooltipContent side="right">
      <p>{label}</p>
    </TooltipContent>
  </Tooltip>
);

const OuterSidebar = () => {
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const {
    subscription,
    isPaid,
    planBadge,
    trialDaysLeft,
    theme,
    toggleTheme,
    handleLogout,
    handleManageSubscription,
    connectAccount,
    isAtAccountLimit,
  } = useAccountActions();

  // When a grant goes dead (revoked/expired), the global query handler raises a
  // "Reconnect" toast that dispatches this event. Re-run the Google consent flow
  // to refresh the stored token for that account.
  useEffect(() => {
    const onReauth = () => connectAccount();
    window.addEventListener("taskfusion:reauth", onReauth);
    return () => window.removeEventListener("taskfusion:reauth", onReauth);
  }, [connectAccount]);

  const handleAddAccountClick = () => {
    if (isAtAccountLimit) {
      setShowUpgradeDialog(true);
      return;
    }
    connectAccount();
  };

  return (
    <>
      <div className="flex flex-col items-center w-16 py-4 bg-muted dark:bg-background border-r max-md:hidden">
        <div className="mb-6">
          <img className="h-8 w-8 object-contain" src={logo} alt="logo" />
        </div>


        <div className="mt-auto flex flex-col items-center gap-2">
          <NavButton
            id="add-account-button"
            icon={Plus}
            label="Add Account"
            isActive={false}
            onClick={handleAddAccountClick}
          />
          {isPaid && (
            <NavButton
              icon={CreditCard}
              label="Manage Subscription"
              isActive={false}
              onClick={handleManageSubscription}
            />
          )}
          <NavButton
            icon={theme === "dark" ? Sun : Moon}
            label={theme === "dark" ? "Light mode" : "Dark mode"}
            isActive={false}
            onClick={toggleTheme}
          />
          <NavButton
            icon={LogOut}
            label="Logout"
            isActive={false}
            onClick={() => setShowLogoutDialog(true)}
          />
          {isPaid ? (
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 text-white text-[10px] font-semibold px-2 py-0.5 rounded mt-2">
              {planBadge}
            </span>
          ) : subscription?.trialActive ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="bg-gradient-to-r from-amber-500 to-orange-500 text-white text-[10px] font-semibold px-2 py-0.5 rounded mt-2 cursor-pointer hover:brightness-110"
                  onClick={() => setShowUpgradeDialog(true)}
                >
                  TRIAL{trialDaysLeft != null ? ` ${trialDaysLeft}d` : ""}
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>{trialDaysLeft != null ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left in your free trial — click to see plans` : "Free trial — click to see plans"}</p>
              </TooltipContent>
            </Tooltip>
          ) : (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  className="bg-muted-foreground/20 text-muted-foreground text-[10px] font-semibold px-2 py-0.5 rounded mt-2 cursor-pointer hover:bg-muted-foreground/30"
                  onClick={() => setShowUpgradeDialog(true)}
                >
                  FREE
                </button>
              </TooltipTrigger>
              <TooltipContent side="right">
                <p>You're on the Free plan — click to see plans</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>

      <Dialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirm Logout</DialogTitle>
            <DialogDescription>
              Are you sure you want to log out?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setShowLogoutDialog(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              Logout
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <UpgradeDialog
        open={showUpgradeDialog}
        onOpenChange={setShowUpgradeDialog}
        source="add_account"
      />
    </>
  );
};

export default OuterSidebar;
